import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle, Check, GalleryHorizontal, Loader2,
} from 'lucide-react';
import { SiFacebook, SiInstagram, SiTiktok, SiYoutube } from 'react-icons/si';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { mapPublishError } from '@/lib/publishing/mapPublishError';
import { resolvePublishBadge } from '@/lib/publishing/resolvePublishBadge';
import type { PublishPlatform, PublishUiStatus } from '@/lib/publishing/types';
import { cn } from '@/lib/utils';

export type OverlayPublishStatus = {
  platform: PublishPlatform;
  status?: PublishUiStatus;
  failReason?: string | null;
  /** `SocialPublish.media_kind`; `'carousel'` adds the carousel icon. */
  mediaKind?: string | null;
};

interface Props {
  statuses: OverlayPublishStatus[];
  className?: string;
}

const PlatformIcon = ({ platform }: { platform: PublishPlatform }) => {
  if (platform === 'youtube') return <SiYoutube className="h-3 w-3 text-[#ff0000]" />;
  if (platform === 'instagram') return <SiInstagram className="h-3 w-3 text-[#e1306c]" />;
  if (platform === 'facebook') {
    return (
      <SiFacebook
        className="h-3 w-3 text-[#1877f2]"
        aria-hidden="true"
        data-testid="publish-status-icon-facebook"
      />
    );
  }
  return <SiTiktok className="h-3 w-3 text-white" />;
};

export const PublishStatusOverlay: React.FC<Props> = ({ statuses, className }) => {
  const { t } = useTranslation();
  const chips = statuses.flatMap((entry) => {
    const badge = resolvePublishBadge(entry.platform, entry.status ?? 'none');
    if (!badge) return [];
    const label = t(badge.labelKey);
    const reason = badge.kind === 'failed' ? t(mapPublishError(entry.failReason)) : null;
    return [{
      platform: entry.platform,
      kind: badge.kind,
      carousel: entry.mediaKind === 'carousel',
      label,
      reason,
    }];
  });

  if (chips.length === 0) return null;

  return (
    <TooltipProvider delayDuration={150}>
    <div
      data-testid="publish-status-overlay"
      className={cn('pointer-events-auto flex flex-col items-start gap-1', className)}
      onClick={(event) => event.stopPropagation()}
    >
      {chips.map((chip) => {
        const detail = chip.reason ? `${chip.label}: ${chip.reason}` : chip.label;
        return (
          <Tooltip key={chip.platform}>
            <TooltipTrigger asChild>
              <button
                type="button"
                data-testid={`publish-status-chip-${chip.platform}`}
                aria-label={detail}
                className={cn(
                  'inline-flex h-7 items-center gap-1 rounded-full border px-1.5 shadow-sm backdrop-blur-sm',
                  chip.kind === 'failed'
                    ? 'border-red-400/40 bg-red-950/70'
                    : 'border-white/15 bg-black/65',
                )}
              >
                <PlatformIcon platform={chip.platform} />
                {chip.carousel && (
                  <GalleryHorizontal
                    className="h-3 w-3 text-white"
                    aria-hidden="true"
                    data-testid={`publish-status-carousel-icon-${chip.platform}`}
                  />
                )}
                {chip.kind === 'publishing' && (
                  <Loader2 className="h-3 w-3 animate-spin text-white" aria-hidden="true" />
                )}
                {chip.kind === 'success' && (
                  <Check className="h-3 w-3 text-emerald-400" aria-hidden="true" />
                )}
                {chip.kind === 'failed' && (
                  <AlertCircle className="h-3 w-3 text-red-400" aria-hidden="true" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[220px] text-xs">
              <p className="font-medium">{chip.label}</p>
              {chip.reason && (
                <p className="mt-0.5 font-normal text-muted-foreground">{chip.reason}</p>
              )}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
    </TooltipProvider>
  );
};

export default PublishStatusOverlay;
